import { useMutation, useQueryClient } from "@tanstack/react-query";
import { berserkr } from "../lib/supabase";

export interface ToggleInclusionArgs {
  caseId: string;
  itemKind: "evidence" | "filing";
  itemId: string;
  included: boolean;
  reason?: string | null;
}

/**
 * Toggle a piece of evidence on/off for the case. Reversible + audit-logged
 * server-side. On success, invalidates every view that respects inclusion so
 * the timeline, web, scoreboard, and inventory all refresh.
 */
export function useToggleInclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: ToggleInclusionArgs) => {
      const { data, error } = await berserkr().rpc("set_evidence_inclusion", {
        p_case_id: args.caseId,
        p_item_kind: args.itemKind,
        p_item_id: args.itemId,
        p_included: args.included,
        p_reason: args.reason ?? null,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ["case-evidence-inventory", args.caseId] });
      qc.invalidateQueries({ queryKey: ["case-timeline", args.caseId] });
      qc.invalidateQueries({ queryKey: ["case-web", args.caseId] });
      qc.invalidateQueries({ queryKey: ["corpus-scoreboard", args.caseId] });
    },
  });
}
